/**
 * LR Direct guided wizard — VOICE builder shared types (Phase A: parser brain).
 *
 * The AI (voice-post "builder" / "builder_correct" modes) only INTERPRETS
 * language: it returns structured stops with RAW date phrases (date_text) and
 * ambiguities. CODE owns calendar dates (resolveDatePhrase) and all state
 * mutation (applyVoiceOps). Nothing here touches saveDirectLoadPost.
 */

export type VoiceStopType = "pickup" | "drop";

export interface VoiceBuilderStop {
  /** Stable unique id, assigned by CODE at parse time — never by the AI. */
  id: string;
  stop_type: VoiceStopType;
  city: string;
  /** 2-letter US state code, "" when not spoken. */
  state: string;
  /** Raw spoken date phrase ("Thursday", "August 28"). NEVER a computed date. */
  date_text: string | null;
}

export interface VoiceAmbiguity {
  /** Which part of the load is uncertain ("stop", "weight", "date", ...). */
  field: string;
  /** The exact phrase that was heard. */
  heard: string;
  candidates: string[];
}

export interface VoiceBuilderLoad {
  /** Ordered stops — pickups then drops (spoken/route order). */
  stops: VoiceBuilderStop[];
  equipment: string | null;
  weight_lbs: number | null;
  length_ft: number | null;
  commodity: string | null;
  temperature_f: number | null;
  handling: string[];
  notes: string | null;
  ambiguities: VoiceAmbiguity[];
}

export const emptyVoiceBuilderLoad = (): VoiceBuilderLoad => ({
  stops: [],
  equipment: null,
  weight_lbs: null,
  length_ft: null,
  commodity: null,
  temperature_f: null,
  handling: [],
  notes: null,
  ambiguities: [],
});


/** Stable stop id generator (code-side, injectable in tests). */
export const newVoiceStopId = (): string =>
  `stop_${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;

// ---------------------------------------------------------------------------
// Correction ops — STRICT allowlist. Anything outside is rejected, never applied.
// ---------------------------------------------------------------------------

export const VOICE_OP_TYPES = [
  "set_equipment",
  "set_weight",
  "set_length",
  "set_commodity",
  "set_temperature",
  "set_notes",
  "set_pickup_date",
  "set_delivery_date",
  "add_stop",
  "update_stop",
  "remove_stop",
  "set_handling",
] as const;


export type VoiceOpType = (typeof VOICE_OP_TYPES)[number];

export type VoiceOpPosition =
  | "first_pickup"
  | "last_pickup"
  | "first_drop"
  | "last_drop";

/**
 * One correction op. Flat shape (the AI emits this; Gemini tool-calling handles
 * a flat object with optional fields far better than a discriminated union).
 * The reducer enforces which fields each op actually requires.
 */
export interface VoiceOp {
  op: string;
  /** Target stop for update_stop / remove_stop / per-stop date ops. */
  stop_id?: string | null;
  /** For add_stop (and update_stop type changes). */
  stop_type?: string | null;
  city?: string | null;
  state?: string | null;
  /** Scalar payload for set_equipment / set_weight / set_commodity /
   *  set_temperature / set_notes / set_handling. */
  value?: string | number | null;
  /** Raw spoken date phrase for the date ops — resolved by CODE. */
  date_text?: string | null;
  position?: string | null;
  /** set_handling: "add" | "remove" (default add). */
  action?: string | null;
}

/** Same caps as the post form / wizard. */
export const VOICE_MAX_PICKUPS = 5;
export const VOICE_MAX_DROPS = 5;

/** Normalize the AI's stop-type vocabulary to the canonical pickup|drop. */
export const normalizeStopType = (raw: string | null | undefined): VoiceStopType | null => {
  const s = (raw || "").trim().toLowerCase();
  if (s === "pickup" || s === "pick" || s === "pu") return "pickup";
  if (s === "drop" || s === "delivery" || s === "del" || s === "deliver") return "drop";
  return null;
};

/** Normalize position aliases ("last_delivery" → "last_drop"). */
export const normalizePosition = (
  raw: string | null | undefined,
): VoiceOpPosition | null => {
  const s = (raw || "").trim().toLowerCase();
  switch (s) {
    case "first_pickup":
      return "first_pickup";
    case "last_pickup":
      return "last_pickup";
    case "first_drop":
    case "first_delivery":
      return "first_drop";
    case "last_drop":
    case "last_delivery":
      return "last_drop";
    default:
      return null;
  }
};
